/**
 * Reusable Skeleton Shimmer Components
 * 
 * Each skeleton matches the EXACT layout dimensions of its corresponding
 * Body component so there's zero layout shift when real content loads.
 * 
 * Uses Tailwind's animate-pulse for the shimmer effect.
 */

// ─── Hero Skeleton ──────────────────────────────────────────

export const HeroSkeleton = () => (
  <section className="relative min-h-[100svh] overflow-hidden bg-slate-900">
    <div className="absolute inset-0 bg-black/40" />
    <div className="relative z-10 min-h-[100svh] flex flex-col justify-center items-start px-6 sm:pl-16 md:pl-24 lg:pl-32 xl:pl-48 pr-6 pt-32 pb-10 md:pt-24 md:pb-12">
      <div className="w-full max-w-3xl animate-pulse">
        {/* Top Bar */}
        <div className="h-8 sm:h-10 w-64 bg-white/20 rounded-full mb-4 sm:mb-8 backdrop-blur-sm" />
        {/* Main Heading */}
        <div className="space-y-4 mb-4 sm:mb-6">
          <div className="h-12 sm:h-16 md:h-20 lg:h-24 w-full bg-white/20 rounded-2xl" />
          <div className="h-12 sm:h-16 md:h-20 lg:h-24 w-4/5 bg-white/20 rounded-2xl" />
        </div>
        {/* Subheading */}
        <div className="space-y-3 mb-8">
          <div className="h-4 sm:h-5 md:h-6 w-full bg-white/20 rounded-lg" />
          <div className="h-4 sm:h-5 md:h-6 w-5/6 bg-white/20 rounded-lg" />
        </div>
        {/* Buttons */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mt-6 sm:mt-8">
          <div className="h-12 sm:h-14 md:h-16 w-40 sm:w-48 bg-white/20 rounded-full" />
          <div className="h-12 sm:h-14 w-32 bg-white/20 rounded-full" />
        </div>
      </div>
    </div>
  </section>
);

// ─── Body2 Skeleton (Offer bar + Cards + Image) ─────────────

export const Body2Skeleton = () => (
  <>
    {/* Offer Bar Skeleton */}
    <div className="bg-gradient-to-r from-orange-500 to-orange-600 py-3 sm:py-4">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-center gap-6 sm:gap-12 lg:gap-36 pl-12 pr-4 sm:px-0 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/20" />
              <div>
                <div className="w-24 h-4 bg-white/20 rounded mb-1" />
                <div className="w-36 h-3 bg-white/20 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Main section skeleton */}
    <section className="bg-white py-10 sm:py-12 lg:py-16 px-6 sm:px-10 md:px-16 lg:px-20 xl:px-32">
      {/* Heading */}
      <div className="max-w-4xl mx-auto text-center mb-8 sm:mb-12 animate-pulse">
        <div className="h-8 sm:h-10 md:h-12 w-3/4 bg-gray-200 rounded-xl mx-auto mb-4" />
        <div className="w-20 sm:w-24 h-1 bg-gray-200 mx-auto mt-6" />
      </div>

      {/* Cards + Image */}
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-6 lg:gap-8 items-stretch mt-10 sm:mt-14 animate-pulse">
        {/* Cards grid */}
        <div className="w-full lg:w-1/2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-gray-50 rounded-2xl px-3 py-4 sm:px-4 sm:py-5">
                <div className="w-11 h-11 bg-gray-200 rounded-xl mb-3" />
                <div className="w-3/4 h-5 bg-gray-200 rounded mb-2" />
                <div className="w-full h-3 bg-gray-200 rounded mb-1" />
                <div className="w-2/3 h-3 bg-gray-200 rounded" />
              </div>
            ))}
          </div>
        </div>
        {/* Image */}
        <div className="w-full lg:w-1/2">
          <div className="w-full h-full min-h-64 bg-gray-200 rounded-2xl" />
        </div>
      </div>

      {/* CTA */}
      <div className="flex justify-center mt-12 sm:mt-16 animate-pulse">
        <div className="w-56 h-12 bg-gray-200 rounded-full" />
      </div>
    </section>
  </>
);

// ─── Body3 Skeleton (Step Flow) ─────────────────────────────

export const Body3Skeleton = () => (
  <section className="py-10 sm:py-12 lg:py-16 px-6 sm:px-10 md:px-16 lg:px-20 xl:px-32 bg-orange-50/50 overflow-hidden">
    {/* Heading */}
    <div className="max-w-4xl mx-auto text-center mb-12 sm:mb-16 animate-pulse">
      <div className="h-8 sm:h-10 md:h-12 w-2/3 bg-gray-200 rounded-xl mx-auto mb-4" />
      <div className="h-4 sm:h-5 w-1/2 bg-gray-200 rounded-lg mx-auto" />
    </div>

    {/* Content */}
    <div className="max-w-6xl mx-auto flex flex-col lg:flex-row items-start lg:items-center gap-10 lg:gap-12 lg:translate-x-14 animate-pulse">
      {/* Left - Steps */}
      <div className="w-full lg:w-1/2">
        {/* Step indicators */}
        <div className="flex items-center justify-between mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="w-12 h-12 rounded-full bg-gray-200" />
          ))}
        </div>
        {/* Card */}
        <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-xl border border-gray-100">
          <div className="w-20 h-6 bg-gray-200 rounded-full mb-4" />
          <div className="flex items-start gap-4 mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gray-200" />
            <div className="flex-1">
              <div className="w-3/4 h-6 bg-gray-200 rounded mb-2" />
              <div className="w-1/2 h-4 bg-gray-200 rounded" />
            </div>
          </div>
          <div className="w-full h-4 bg-gray-200 rounded mb-2" />
          <div className="w-5/6 h-4 bg-gray-200 rounded mb-2" />
          <div className="w-2/3 h-4 bg-gray-200 rounded" />
        </div>
      </div>

      {/* Right - Phone mockup */}
      <div className="w-full lg:w-1/2 flex justify-center">
        <div className="w-[220px] sm:w-[300px] h-[450px] bg-gray-200 rounded-2xl" />
      </div>
    </div>
  </section>
);

// ─── Body4 Skeleton (Feature Checklist) ─────────────────────

export const Body4Skeleton = () => (
  <section className="py-10 sm:py-12 lg:py-16 px-6 sm:px-10 md:px-16 lg:px-20 xl:px-32 bg-white">
    {/* Heading */}
    <div className="max-w-4xl mx-auto text-center mb-10 sm:mb-14 animate-pulse">
      <div className="h-8 sm:h-10 md:h-12 w-3/4 bg-gray-200 rounded-xl mx-auto mb-4" />
      <div className="h-4 sm:h-5 w-2/3 bg-gray-200 rounded-lg mx-auto" />
    </div>

    {/* Feature grid */}
    <div className="max-w-5xl mx-auto animate-pulse">
      <div className="bg-white rounded-3xl shadow-md border border-gray-100 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-2">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className={`flex items-start gap-5 p-8 sm:p-10 lg:p-12 ${i < 5 ? 'border-b border-gray-100' : ''} ${i % 2 !== 0 ? 'md:border-r border-gray-100' : ''}`}>
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gray-200 flex-shrink-0" />
              <div className="flex-1">
                <div className="w-3/4 h-5 bg-gray-200 rounded mb-3" />
                <div className="w-full h-4 bg-gray-200 rounded mb-1" />
                <div className="w-2/3 h-4 bg-gray-200 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* CTA */}
    <div className="text-center mt-12 sm:mt-16 animate-pulse">
      <div className="w-56 h-12 bg-gray-200 rounded-full mx-auto" />
      <div className="w-64 h-4 bg-gray-200 rounded mx-auto mt-4" />
    </div>
  </section>
);

// ─── Body5 Skeleton (Popular Bentos Carousel) ───────────────

export const Body5Skeleton = () => (
  <section className="py-10 sm:py-12 lg:py-16 px-6 sm:px-10 md:px-16 lg:px-20 xl:px-32 bg-orange-50/50">
    {/* Heading */}
    <div className="max-w-4xl mx-auto text-center mb-10 sm:mb-14 animate-pulse">
      <div className="h-8 sm:h-10 md:h-12 w-2/3 bg-gray-200 rounded-xl mx-auto" />
    </div>

    {/* Cards */}
    <div className="max-w-7xl mx-auto animate-pulse">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm">
            <div className="h-48 sm:h-52 bg-gray-200" />
            <div className="p-4 sm:p-5">
              <div className="w-3/4 h-5 bg-gray-200 rounded mb-2" />
              <div className="w-1/2 h-3 bg-gray-200 rounded mb-3" />
              <div className="w-full h-3 bg-gray-200 rounded mb-1" />
              <div className="w-5/6 h-3 bg-gray-200 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Body6 Skeleton (Kitchen Story) ─────────────────────────

export const Body6Skeleton = () => (
  <section className="py-10 sm:py-12 lg:py-16 px-6 sm:px-10 md:px-16 lg:px-20 xl:px-32 bg-white">
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center animate-pulse">
        {/* Image */}
        <div className="w-full lg:w-1/2">
          <div className="w-full h-80 sm:h-96 lg:h-[450px] bg-gray-200 rounded-2xl" />
        </div>
        {/* Content */}
        <div className="w-full lg:w-1/2">
          <div className="w-40 h-4 bg-gray-200 rounded mb-4" />
          <div className="w-full h-10 bg-gray-200 rounded-xl mb-3" />
          <div className="w-4/5 h-10 bg-gray-200 rounded-xl mb-6" />
          <div className="w-full h-4 bg-gray-200 rounded mb-2" />
          <div className="w-full h-4 bg-gray-200 rounded mb-2" />
          <div className="w-3/4 h-4 bg-gray-200 rounded mb-8" />
          <div className="w-52 h-12 bg-gray-200 rounded-full" />
        </div>
      </div>
    </div>
  </section>
);

// ─── Body7 Skeleton (Customer Reviews) ──────────────────────

export const Body7Skeleton = () => (
  <section className="py-16 sm:py-24 bg-[#FFFCF8] overflow-hidden">
    {/* Header */}
    <div className="max-w-4xl mx-auto text-center mb-10 sm:mb-16 px-6 animate-pulse">
      <div className="w-40 h-4 bg-gray-200 rounded mx-auto mb-4" />
      <div className="h-10 sm:h-14 w-3/4 bg-gray-200 rounded-xl mx-auto mb-4" />
      <div className="h-5 w-2/3 bg-gray-200 rounded-lg mx-auto" />
    </div>

    {/* Cards */}
    <div className="flex gap-4 sm:gap-6 px-4 pb-10 overflow-hidden animate-pulse">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="w-[280px] sm:w-[380px] md:w-[420px] flex-shrink-0 bg-white rounded-3xl p-5 sm:p-8 border border-orange-100/50 shadow-sm">
          <div className="flex items-center gap-3 sm:gap-4 mb-3 sm:mb-5">
            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-full bg-gray-200" />
            <div className="flex-1">
              <div className="w-28 h-5 bg-gray-200 rounded mb-1" />
              <div className="w-40 h-3 bg-gray-200 rounded" />
            </div>
          </div>
          <div className="flex gap-1 mb-3 sm:mb-4">
            {[1, 2, 3, 4, 5].map((s) => (
              <div key={s} className="w-4 h-4 bg-gray-200 rounded" />
            ))}
          </div>
          <div className="w-full h-4 bg-gray-200 rounded mb-2" />
          <div className="w-full h-4 bg-gray-200 rounded mb-2" />
          <div className="w-2/3 h-4 bg-gray-200 rounded" />
        </div>
      ))}
    </div>

    {/* Footer text */}
    <div className="text-center mt-2 sm:mt-4 px-6 animate-pulse">
      <div className="w-64 h-4 bg-gray-200 rounded mx-auto" />
    </div>
  </section>
);

// ─── Body8 Skeleton (Blog Cards) ────────────────────────────

export const Body8Skeleton = () => (
  <section className="py-10 sm:py-12 lg:py-16 bg-white overflow-hidden">
    {/* Heading + Nav */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 lg:px-10 xl:px-6 mb-10 sm:mb-14 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="h-8 sm:h-10 md:h-12 w-64 bg-gray-200 rounded-xl" />
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gray-200" />
          <div className="w-12 h-12 rounded-full bg-gray-200" />
        </div>
      </div>
    </div>

    {/* Cards */}
    <div className="max-w-7xl xl:max-w-[1350px] 2xl:max-w-[1450px] mx-auto px-4 sm:px-6 md:px-8 lg:px-10 xl:px-12 animate-pulse">
      <div className="flex gap-6 overflow-hidden pb-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex-shrink-0 w-[340px] sm:w-[360px]">
            <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100">
              <div className="h-52 bg-gray-200" />
              <div className="p-5">
                <div className="w-20 h-6 bg-gray-200 rounded-full mb-3" />
                <div className="w-full h-5 bg-gray-200 rounded mb-2" />
                <div className="w-3/4 h-5 bg-gray-200 rounded mb-3" />
                <div className="flex items-center gap-3 mt-4">
                  <div className="w-8 h-8 rounded-full bg-gray-200" />
                  <div className="w-32 h-4 bg-gray-200 rounded" />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Body9 Skeleton (CTA Bottom Bar) ────────────────────────

export const Body9Skeleton = () => (
  <section className="pt-10 sm:pt-12 lg:pt-16 px-6 sm:px-10 md:px-16 lg:px-20 xl:px-32 bg-orange-50/50 overflow-hidden">
    <div className="max-w-6xl mx-auto text-center animate-pulse">
      {/* Tag */}
      <div className="w-48 h-8 bg-gray-200 rounded-full mx-auto mb-6" />
      {/* Heading */}
      <div className="h-10 sm:h-14 w-2/3 bg-gray-200 rounded-xl mx-auto mb-4" />
      {/* Subheading */}
      <div className="h-5 w-1/2 bg-gray-200 rounded-lg mx-auto mb-8" />
      {/* Buttons */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-12 sm:mb-16">
        <div className="w-52 h-12 bg-gray-200 rounded-full" />
        <div className="w-44 h-12 bg-gray-200 rounded-full" />
      </div>
      {/* Image area */}
      <div className="max-w-4xl mx-auto">
        <div className="w-full h-64 bg-gray-200 rounded-xl" />
      </div>
    </div>
  </section>
);

// ─── AboutUs Skeleton ───────────────────────────────────────

export const AboutUsSkeleton = () => (
  <section className="!pt-28 md:!pt-32 bg-gradient-to-b from-orange-50/50 to-white overflow-hidden">
    {/* Story Section */}
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 md:pt-12 md:pb-16 animate-pulse">
      {/* Header */}
      <div className="text-center mb-12 md:mb-16">
        <div className="w-32 h-4 bg-gray-200 rounded mx-auto mb-4" />
        <div className="w-2/3 max-w-2xl h-10 sm:h-12 md:h-14 bg-gray-200 rounded-xl mx-auto" />
      </div>

      {/* Story Content */}
      <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-16">
        <div className="w-full lg:w-1/2">
          <div className="w-full h-64 md:h-80 lg:h-96 bg-gray-200 rounded-3xl" />
        </div>
        <div className="w-full lg:w-1/2 space-y-4">
          <div className="w-full h-4 bg-gray-200 rounded" />
          <div className="w-full h-4 bg-gray-200 rounded" />
          <div className="w-5/6 h-4 bg-gray-200 rounded" />
          <div className="w-full h-4 bg-gray-200 rounded" />
          <div className="w-3/4 h-4 bg-gray-200 rounded" />
        </div>
      </div>
    </div>

    {/* Orange Mission Section */}
    <div className="bg-gray-200 py-12 md:py-16 animate-pulse">
      <div className="max-w-4xl mx-auto px-4 text-center space-y-4">
        <div className="w-48 h-8 bg-white/40 rounded-lg mx-auto" />
        <div className="w-3/4 h-6 bg-white/40 rounded-lg mx-auto" />
        <div className="w-2/3 h-6 bg-white/40 rounded-lg mx-auto" />
      </div>
    </div>

    {/* Core Values Section */}
    <div className="py-16 md:py-20 animate-pulse">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="w-32 h-4 bg-gray-200 rounded mx-auto mb-4" />
          <div className="w-64 h-10 bg-gray-200 rounded-xl mx-auto" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm">
              <div className="w-12 h-12 bg-gray-200 rounded-xl mb-4" />
              <div className="w-3/4 h-6 bg-gray-200 rounded mb-3" />
              <div className="w-full h-4 bg-gray-200 rounded mb-2" />
              <div className="w-full h-4 bg-gray-200 rounded mb-2" />
              <div className="w-2/3 h-4 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// ─── ContactUs Skeleton ─────────────────────────────────────

export const ContactUsSkeleton = () => (
  <section className="!pt-28 md:!pt-32 bg-white">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12 md:pt-12 md:pb-16 animate-pulse">
      {/* Header */}
      <div className="w-64 h-12 bg-gray-200 rounded-xl mx-auto mb-12 md:mb-16" />

      {/* Map + Form Row */}
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 mb-16">
        {/* Map Skeleton */}
        <div className="w-full lg:w-1/2">
          <div className="w-full h-80 md:h-96 lg:h-[420px] bg-gray-200 rounded-2xl" />
        </div>
        
        {/* Form Skeleton */}
        <div className="w-full lg:w-1/2 flex flex-col justify-center space-y-6">
          <div className="w-full h-14 bg-gray-100 rounded-xl" />
          <div className="w-full h-14 bg-gray-100 rounded-xl" />
          <div className="w-full h-14 bg-gray-100 rounded-xl" />
          <div className="w-full h-32 bg-gray-100 rounded-xl" />
          <div className="w-full h-14 bg-gray-200 rounded-xl" />
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-12 lg:gap-20 pt-8 border-t border-gray-100">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-gray-100" />
            <div>
              <div className="w-20 h-5 bg-gray-200 rounded mb-2" />
              <div className="w-32 h-4 bg-gray-100 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── FAQ Skeleton ───────────────────────────────────────────

export const FAQSkeleton = () => (
  <section className="!pt-28 md:!pt-32 bg-gradient-to-b from-gray-50/50 to-white overflow-hidden">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pt-12 md:pb-24 animate-pulse">
      {/* Header */}
      <div className="w-80 h-12 bg-gray-200 rounded-xl mx-auto mb-12 md:mb-16" />

      {/* Content */}
      <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
        {/* FAQ Accordion */}
        <div className="w-full lg:w-2/3">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="border-b border-gray-100 last:border-b-0 py-5 px-6 flex justify-between items-center">
                <div className="w-3/4 h-5 bg-gray-200 rounded" />
                <div className="w-4 h-4 bg-gray-200 rounded-full" />
              </div>
            ))}
          </div>
        </div>

        {/* More Questions Card */}
        <div className="w-full lg:w-1/3">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 md:p-8 text-center flex flex-col items-center">
            <div className="w-16 h-16 bg-gray-200 rounded-xl mb-4" />
            <div className="w-3/4 h-6 bg-gray-200 rounded mb-3" />
            <div className="w-full h-4 bg-gray-200 rounded mb-2" />
            <div className="w-2/3 h-4 bg-gray-200 rounded mb-6" />
            <div className="w-full h-12 bg-gray-200 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// ─── Blog Skeleton ─────────────────────────────────────────

export const BlogSkeleton = () => (
  <section className="!pt-28 md:!pt-32 bg-gradient-to-b from-gray-50/50 to-white min-h-screen">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pt-12 md:pb-24 animate-pulse">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="w-48 h-12 bg-gray-200 rounded-xl mx-auto mb-4" />
        <div className="w-32 h-4 bg-gray-200 rounded mx-auto" />
      </div>

      {/* Featured Post */}
      <div className="mb-12">
        <div className="w-full h-80 md:h-96 lg:h-[450px] bg-gray-200 rounded-2xl" />
      </div>

      {/* Blog Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div key={i} className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm">
            <div className="w-full h-48 bg-gray-200" />
            <div className="p-5">
              <div className="w-20 h-6 bg-gray-200 rounded-full mb-3" />
              <div className="w-full h-6 bg-gray-200 rounded mb-2" />
              <div className="w-3/4 h-6 bg-gray-200 rounded mb-4" />
              <div className="flex items-center gap-3 mt-4">
                <div className="w-8 h-8 rounded-full bg-gray-200" />
                <div className="w-32 h-4 bg-gray-200 rounded" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Locations Skeleton ────────────────────────────────────

export const LocationsSkeleton = () => (
  <section className="!pt-28 md:!pt-32 bg-gradient-to-b from-orange-50/50 to-white ">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 md:pt-12 md:pb-24 animate-pulse">
      {/* Header */}
      <div className="text-center mb-12 md:mb-16">
        <div className="w-32 h-4 bg-gray-200 rounded mx-auto mb-4" />
        <div className="w-64 h-12 bg-gray-200 rounded-xl mx-auto" />
      </div>

      {/* Locations List */}
      <div className="space-y-10">
        {[1, 2, 3].map((i) => (
          <div key={i} className="relative rounded-2xl overflow-hidden shadow-lg border border-gray-100">
            {/* Background Image */}
            <div className="w-full h-72 md:h-80 lg:h-96 bg-gray-200" />

            {/* Info Card - Positioned at bottom right */}
            <div className="absolute bottom-4 right-4 left-4 md:left-auto md:w-80 bg-white rounded-xl shadow-xl p-5">
              <div className="w-32 h-6 bg-gray-200 rounded mb-4" />
              <div className="w-full h-16 bg-gray-100 rounded mb-4" />
              <div className="w-3/4 h-5 bg-gray-100 rounded mb-3" />
              <div className="w-1/2 h-5 bg-gray-100 rounded mb-3" />
              <div className="w-2/3 h-5 bg-gray-100 rounded" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── BulkOrder Skeleton ────────────────────────────────────

export const BulkOrderSkeleton = () => (
  <section className="min-h-[100svh] w-full bg-[#FEF2F2] flex items-center justify-center relative overflow-hidden">
    <div className="absolute inset-0 z-0 flex">
      <div className="absolute inset-0 bg-catering-gradient md:bg-[linear-gradient(105deg,#FFF6EF_0%,#FFF6EF_55%,transparent_65%)] z-10 w-full h-full pointer-events-none opacity-90 md:opacity-100"></div>
      <div className="w-full h-full md:w-full md:h-full ml-auto overflow-hidden absolute right-0 top-0">
        <div className="w-full h-full bg-gray-200 opacity-40 md:opacity-100" />
      </div>
    </div>
    
    <div className="container mx-auto px-6 md:px-12 lg:px-24 z-20 h-full flex flex-col justify-center pt-32 pb-10 md:py-20 relative">
      <div className="max-w-2xl w-full animate-pulse">
        {/* Badge */}
        <div className="h-8 w-64 bg-gray-200/80 rounded-full mb-6" />
        {/* Heading */}
        <div className="h-16 md:h-24 w-3/4 bg-gray-200/80 rounded-2xl mb-4" />
        <div className="h-16 md:h-24 w-full bg-gray-200/80 rounded-2xl mb-6" />
        {/* Description */}
        <div className="h-6 w-full bg-gray-200/80 rounded-lg mb-2" />
        <div className="h-6 w-5/6 bg-gray-200/80 rounded-lg mb-8" />
        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 mb-16">
          <div className="h-14 w-48 bg-gray-200/80 rounded-xl" />
          <div className="h-14 w-48 bg-gray-200/80 rounded-xl" />
        </div>
        {/* Features Bottom Row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gray-200/80" />
              <div>
                <div className="w-24 h-4 bg-gray-200/80 rounded mb-1" />
                <div className="w-16 h-3 bg-gray-200/80 rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
